import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRegistry, getRegistryCount } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../lib/utils';
import { ChevronLeft, ChevronRight, ExternalLink, Building2, Globe, Info, Shield, FileText, CheckCircle2 } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

// AASC Status display helper - exact labels from authority site
const getStatusDisplay = (status) => {
  switch (status) {
    case 'declared':
      return { label: 'declared', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Shield };
    case 'alignment_declared':
      return { label: 'Alignment Declared', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: FileText };
    case 'conditionally_declared':
      return { label: 'Conditionally declared', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Shield };
    case 'expired':
      return { label: 'Expired', className: 'bg-zinc-100 text-zinc-600 border-zinc-300', icon: null };
    case 'revoked':
      return { label: 'Revoked', className: 'bg-red-50 text-red-700 border-red-200', icon: null };
    default:
      return { label: status, className: 'bg-zinc-100 text-zinc-600 border-zinc-300', icon: null };
  }
};

// Get tier display info
const getTierDisplay = (tier) => {
  if (tier === 'declaration') {
    return { label: 'Tier 1: Operator declaration', color: 'emerald' };
  }
  return { label: 'Tier 2: Standards Alignment Declaration', color: 'blue' };
};

export default function Registry() {
  const [organizations, setOrganizations] = useState([]);
  const [counts, setCounts] = useState({ total: 0, declared: 0, alignment_declared: 0, conditionally_declared: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [registryRes, countRes] = await Promise.all([
        getRegistry(page, limit),
        getRegistryCount()
      ]);
      setOrganizations(registryRes.data);
      setCounts(countRes.data);
    } catch (err) {
      console.error('Failed to load registry:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(counts.total / limit);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="registry-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link nav-link-active">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
              <Link to="/org/login" className="text-sm text-zinc-500 hover:text-zinc-700">Organization Login</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 flex-1">
        <div className="page-header">
          <h1 className="page-title">AASC Registry</h1>
          <p className="page-subtitle">
            Public verification of Standards Alignment Declarations and AASC declarations.
          </p>
        </div>

        {/* Registry Purpose Statement */}
        <div className="bg-zinc-50 border border-zinc-200 p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-600">
              <p>
                The registry provides public verification of status. Status labels defined by the{' '}
                <a 
                  href={`${AUTHORITY_SITE}/standards`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-zinc-900"
                >
                  AI Accountability Standards Council
                </a>: Alignment Declared, declared, Conditionally declared, Revoked, Expired.
              </p>
            </div>
          </div>
        </div>

        {/* Stats - AASC Status Labels */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card">
            <div className="stat-value text-emerald-600">{counts.declared || 0}</div>
            <div className="stat-label">declared</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-blue-600">{counts.alignment_declared || 0}</div>
            <div className="stat-label">Alignment Declared</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-amber-600">{counts.conditionally_declared || 0}</div>
            <div className="stat-label">Conditionally declared</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{counts.total || 0}</div>
            <div className="stat-label">Total Listed</div>
          </div>
        </div>

        {/* Registry List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-zinc-500">Loading...</div>
          </div>
        ) : organizations.length === 0 ? (
          <div className="empty-state border border-zinc-200 p-12">
            <Building2 className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <p>No organizations found.</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {organizations.map(org => {
                const statusInfo = getStatusDisplay(org.registry_status);
                const StatusIcon = statusInfo.icon;
                const tierInfo = getTierDisplay(org.tier);
                
                return (
                  <div 
                    key={org.org_id} 
                    className="border border-zinc-300 bg-white p-6"
                    data-testid={`registry-org-${org.org_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-zinc-900">{org.org_display_name}</h3>
                          <Badge variant="outline" className={`rounded-none ${statusInfo.className}`}>
                            {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
                            {statusInfo.label || org.status_label}
                          </Badge>
                        </div>
                        
                        {/* Tier Label */}
                        <p className="text-sm text-zinc-600 mb-2">{org.tier_label || tierInfo.label}</p>
                        
                        {org.org_website && (
                          <a 
                            href={org.org_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1 mb-3"
                          >
                            <Globe className="w-3 h-3" />
                            {org.org_website.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                        
                        {/* Scope Statement */}
                        {org.scope_statement && (
                          <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                            <p className="text-xs text-zinc-500 mb-1">Scope Statement</p>
                            <p className="text-sm text-zinc-700">{org.scope_statement}</p>
                          </div>
                        )}
                        
                        {/* Accountability Boundaries Verification (Tier 1 only) */}
                        {org.tier === 'declaration' && (
                          <div className="mt-3 flex items-center gap-2">
                            {org.accountability_boundaries_verified ? (
                              <Badge variant="outline" className="rounded-none bg-emerald-50 text-emerald-700 border-emerald-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Accountability Boundaries Verified
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="rounded-none bg-zinc-50 text-zinc-500 border-zinc-200">
                                Accountability Boundaries Pending
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {org.standard_titles && org.standard_titles.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-zinc-500 mb-2">Standards ({org.standards_count})</p>
                            <div className="flex flex-wrap gap-2">
                              {org.standard_titles.map((title, idx) => (
                                <Badge key={idx} variant="outline" className="rounded-none text-xs">
                                  {title}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right ml-6">
                        {org.declaration_id && (
                          <>
                            <div className="cert-id text-sm mb-1">{org.declaration_id}</div>
                            <div className="text-xs text-zinc-500 mb-2">
                              {org.issue_date && `Issued: ${formatDate(org.issue_date)}`}
                            </div>
                            <div className="text-xs text-zinc-500 mb-3">
                              {org.expiry_date && `Expires: ${formatDate(org.expiry_date)}`}
                            </div>
                            <Link 
                              to={`/verify/${org.declaration_id}`}
                              className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
                              data-testid={`verify-${org.declaration_id}`}
                            >
                              Verify <ExternalLink className="w-3 h-3" />
                            </Link>
                          </>
                        )}
                        {org.is_placeholder && (
                          <div className="text-xs text-zinc-400">
                            Not declared
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination justify-center mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="pagination-btn rounded-none"
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-zinc-500 px-4">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="pagination-btn rounded-none"
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-zinc-50 border border-zinc-200 text-sm text-zinc-500">
          <p>
            This platform is not a regulatory body. declaration does not imply endorsement, approval, legal compliance, or performance guarantees. declaration reflects documented conformance to published standards at the time of review.
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
